import { Suspense } from 'react';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Icon,
  Link,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderTitle,
  PageLoader,
  Paragraph,
  Stack,
  Strong,
  Tooltip,
} from '@customerio/pluma-components/react';
import { useLocation } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { DocumentTitle } from '@/components/page/document-title';
import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';
import { useWebhooks } from '@/services/db/webhook/queries';
import { ReportingWebhooksTable } from './components/reporting-webhooks-table';
import { REPORTING_WEBHOOKS_DOCS_URL, useReportingWebhookBreadcrumbs, useReportingWebhookLinks } from './hooks';

/** Client-side cap, exactly as in Ember — the backend does not enforce it. */
const MAX_WEBHOOKS = 10;

type ReportingWebhooksContentProps = {
  workspaceId: number;
  canEdit: boolean;
  canDelete: boolean;
};

function ReportingWebhooksContent({ workspaceId, canEdit, canDelete }: ReportingWebhooksContentProps) {
  // The id arrives already resolved. This renders inside a Suspense boundary, and React can begin
  // rendering it before the parent commits, so it must not read the URL itself.
  const { data: webhooks } = useWebhooks({ workspaceId });
  const links = useReportingWebhookLinks(String(workspaceId));

  if (webhooks.length === 0) {
    return (
      <Stack alignBlock="center" alignInline="center">
        <EmptyState data-test-blank-slate>
          <EmptyStateTitle icon="webhook">Reporting webhooks</EmptyStateTitle>
          <EmptyStateDescription>
            Register up to 10 URLs with Customer.io to receive information about <Strong>Customer</Strong>,{' '}
            <Strong>Email</Strong>, <Strong>SMS</Strong>, <Strong>Push</Strong>, <Strong>Slack</Strong>, and{' '}
            <Strong>Webhook</Strong> events as they occur in real time.
          </EmptyStateDescription>
          <EmptyStateActions>
            {/* Ember does not gate this button on the edit permission, unlike the page-header one. */}
            <Button data-test-add-webhook variant="primary" href={links.newHref}>
              Add reporting webhook
            </Button>
          </EmptyStateActions>
        </EmptyState>
        <Paragraph>
          <Icon name="help" color="subtle" size="sm" mr="100" />
          Learn more about{' '}
          <Link href={REPORTING_WEBHOOKS_DOCS_URL} data-test-documentation-url variant="secondary" isExternal>
            reporting webhooks
          </Link>
        </Paragraph>
      </Stack>
    );
  }

  const isAtCap = webhooks.length >= MAX_WEBHOOKS;

  return (
    <Page>
      <PageHeader>
        <PageHeaderTitle>Reporting webhooks</PageHeaderTitle>
        <PageHeaderDescription>
          Register up to 10 URLs with Customer.io to receive information about Customer, Email, SMS, Push, Slack, and
          Webhook events as they occur in real time.
          <br />
          Details on the format and specific webhooks we deliver are available in{' '}
          <Link href={REPORTING_WEBHOOKS_DOCS_URL} variant="secondary" isExternal>
            our documentation
          </Link>
          .
        </PageHeaderDescription>
        <PageHeaderActions>
          {/* The cap tooltip shows only *at* the cap; lacking permission disables the button silently. */}
          <Tooltip isDisabled={!isAtCap} content="You've added the maximum amount of endpoints." isCustom={true}>
            {(context) => (
              <Button
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
                data-test-add-webhook
                isDisabled={isAtCap || !canEdit}
                variant="primary"
                href={links.newHref}
              >
                Add webhook
              </Button>
            )}
          </Tooltip>
        </PageHeaderActions>
      </PageHeader>
      <PageBody>
        <ReportingWebhooksTable webhooks={webhooks} editHref={links.editHref} canEdit={canEdit} canDelete={canDelete} />
      </PageBody>
    </Page>
  );
}

/**
 * The reporting-webhooks list. Route access (Builder plan, `journeys/imports/view`) is applied by
 * `ReportingWebhooksRouteView` before this mounts, mirroring Ember's `integrations` root route.
 */
export default function ReportingWebhooksPage() {
  // Read off the reactive router location rather than `window.location`: on `/workspaces/last` the
  // id resolves a beat later, and a non-reactive read would never see it land.
  const { pathname } = useLocation();
  const workspaceId = Number(getWorkspaceIdFromUrl(pathname));
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('journeys/reporting_webhooks/edit') === true;
  // Delete has its own narrower permission in Ember — it is not the edit gate.
  const canDelete = hasPermission('journeys/reporting_webhooks/delete') === true;

  useReportingWebhookBreadcrumbs(String(workspaceId));

  if (!Number.isInteger(workspaceId) || workspaceId <= 0) {
    return <PageLoader />;
  }

  return (
    <>
      <DocumentTitle title="Reporting webhooks" />
      <Suspense fallback={<PageLoader />}>
        <ReportingWebhooksContent workspaceId={workspaceId} canEdit={canEdit} canDelete={canDelete} />
      </Suspense>
    </>
  );
}
